import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import PetsRoundedIcon from "@mui/icons-material/PetsRounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.tsx";
import { supabase } from "../supabaseClient.ts";

type Family = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
};

type Dog = {
  id: string;
  name: string;
  breed: string | null;
  sex: string;
  birth_date: string | null;
  neutered: boolean | null;
  chemically_neutered: boolean | null;
  direct_booking_allowed: boolean;
  active: boolean;
  never_in_heat?: boolean | null;
  last_heat_date?: string | null;
  next_heat_month?: string | null;
};

type ReservationDog = {
  dogs: { name: string } | null;
};

type Reservation = {
  id: string;
  booking_type: string;
  start_date: string;
  end_date: string;
  status: string;
  option_expires_at: string | null;
  reservation_dogs: ReservationDog[];
};

function nlDate(value: string) {
  return new Intl.DateTimeFormat("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(`${value}T12:00:00`));
}

function reservationLabel(r: Reservation) {
  if (r.booking_type === "dagopvang" || r.start_date === r.end_date) {
    return `Dagopvang · ${nlDate(r.start_date)}`;
  }

  return `Logeren · ${nlDate(r.start_date)} t/m ${nlDate(r.end_date)}`;
}

function statusLabel(status: string) {
  return (
    {
      confirmed: "Bevestigd",
      option: "Optie",
      cancel_pending_payment: "Wacht op betaling",
      cancelled: "Geannuleerd",
      completed: "Afgerond",
      expired: "Verlopen",
    }[status] ?? status
  );
}

export const CustomerPortalPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [family, setFamily] = useState<Family | null>(null);
  const [dogs, setDogs] = useState<Dog[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    if (!supabase || !user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    const { data: familyData, error: familyError } = await supabase
      .from("families")
      .select("id, first_name, last_name, email")
      .eq("user_id", user.id)
      .maybeSingle();

    if (familyError || !familyData) {
      setError(
        familyError?.message ||
          "Je klantprofiel kon niet worden geladen."
      );
      setLoading(false);
      return;
    }

    setFamily(familyData as Family);

    const [
      { data: dogData, error: dogError },
      { data: reservationData, error: reservationError },
    ] = await Promise.all([
      supabase
        .from("dogs")
        .select(
          "id, name, breed, sex, birth_date, neutered, chemically_neutered, direct_booking_allowed, active, never_in_heat, last_heat_date, next_heat_month"
        )
        .eq("family_id", familyData.id)
        .eq("active", true)
        .order("name", { ascending: true }),

      supabase
        .from("reservations")
        .select(`
          id,
          booking_type,
          start_date,
          end_date,
          status,
          option_expires_at,
          reservation_dogs (
            dogs ( name )
          )
        `)
        .eq("family_id", familyData.id)
        .order("start_date", { ascending: true }),
    ]);

    if (dogError) {
      setError("Je hondgegevens konden niet worden geladen.");
    } else {
      setDogs((dogData ?? []) as Dog[]);
    }

    if (reservationError) {
      setError((prev) =>
        prev
          ? `${prev} Ook je reserveringen konden niet worden geladen.`
          : "Je reserveringen konden niet worden geladen."
      );
    } else {
      setReservations((reservationData ?? []) as Reservation[]);
    }

    setLoading(false);
  }

  useEffect(() => {
    load();
  }, [user]);

  const today = new Date().toISOString().slice(0, 10);

  const upcoming = useMemo(
    () =>
      reservations
        .filter(
          (r) =>
            r.end_date >= today &&
            !["cancelled", "expired"].includes(r.status)
        )
        .sort((a, b) => a.start_date.localeCompare(b.start_date)),
    [reservations, today]
  );

  const nextReservation = upcoming[0] ?? null;

  const bookableDogs = dogs.filter(
    (dog) => dog.direct_booking_allowed
  ).length;

  async function logout() {
    if (!supabase) return;

    setLoggingOut(true);
    const { error: signOutError } = await supabase.auth.signOut();
    setLoggingOut(false);

    if (signOutError) {
      setError("Uitloggen is niet gelukt. Probeer het opnieuw.");
      return;
    }

    navigate("/inloggen", { replace: true });
  }

  if (loading) {
    return (
      <Box sx={{ p: 8, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#fffafb",
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          gap={2}
          alignItems={{ md: "center" }}
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              Mijn Pippaya
            </Typography>

            <Typography variant="h3">
              Welkom{family?.first_name ? `, ${family.first_name}` : ""}
            </Typography>

            <Typography color="text.secondary" sx={{ mt: 0.75 }}>
              Hier vind je jouw honden, reserveringen en alles rondom de
              opvang bij Pippaya.
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<LogoutRoundedIcon />}
              onClick={logout}
              disabled={loggingOut}
            >
              {loggingOut ? "Uitloggen..." : "Uitloggen"}
            </Button>

            <Button
              variant="contained"
              size="large"
              startIcon={<CalendarMonthRoundedIcon />}
              onClick={() => navigate("/mijn-pippaya/reserveren")}
              disabled={bookableDogs === 0}
            >
              Nieuwe reservering
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2.5 }}>
            {error}
          </Alert>
        )}

        {dogs.length > 0 && bookableDogs === 0 && (
          <Alert severity="info" sx={{ mt: 2.5 }}>
            Je Mijn Pippaya-account is actief. Op dit moment staat nog geen
            hond vrij voor rechtstreeks reserveren. Pippaya kan dit per hond
            na beoordeling inschakelen.
          </Alert>
        )}

        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 2.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CalendarMonthRoundedIcon color="primary" />
              <Typography variant="h5" sx={{ mt: 1 }}>
                Nieuwe reservering
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Kies een goedgekeurde hond en vraag dagopvang of logeren aan.
              </Typography>
              <Button
                sx={{ mt: "auto", pt: 2 }}
                onClick={() => navigate("/mijn-pippaya/reserveren")}
                disabled={bookableDogs === 0}
              >
                Reserveren
              </Button>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 2.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ListAltRoundedIcon color="primary" />
              <Typography variant="h5" sx={{ mt: 1 }}>
                Mijn reserveringen
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Bekijk komende boekingen, opties en eerdere opvang.
              </Typography>
              <Button
                sx={{ mt: "auto", pt: 2 }}
                onClick={() => navigate("/mijn-pippaya/reserveringen")}
              >
                Bekijk reserveringen
              </Button>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 2.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <PetsRoundedIcon color="primary" />
              <Typography variant="h5" sx={{ mt: 1 }}>
                Mijn honden
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Bekijk welke honden aan jouw Mijn Pippaya-account gekoppeld zijn.
              </Typography>
              <Button
                sx={{ mt: "auto", pt: 2 }}
                onClick={() => navigate("/mijn-pippaya/honden")}
              >
                Bekijk honden
              </Button>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ p: 2.5, mt: 2.5 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
            gap={1.5}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h5">Mijn honden</Typography>
              <Typography variant="body2" color="text.secondary">
                Alleen honden die door Pippaya zijn vrijgegeven kunnen
                rechtstreeks worden gereserveerd.
              </Typography>
            </Box>

            <Button
              startIcon={<AddRoundedIcon />}
              onClick={() => navigate("/mijn-pippaya/hond-aanmelden")}
            >
              Nieuwe hond aanmelden
            </Button>
          </Stack>

          {dogs.length ? (
            <Grid container spacing={2}>
              {dogs.map((dog) => (
                <Grid item xs={12} sm={6} key={dog.id}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      boxShadow: "none",
                      height: "100%",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <PetsRoundedIcon color="primary" />

                      <Box sx={{ flex: 1 }}>
                        <Typography fontWeight={600}>
                          {dog.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          {dog.breed || "Ras niet ingevuld"} ·{" "}
                          {dog.sex === "reu" ? "reu" : "teef"}
                        </Typography>
                      </Box>

                      <Chip
                        size="small"
                        icon={
                          dog.direct_booking_allowed ? (
                            <CheckCircleRoundedIcon />
                          ) : (
                            <HourglassTopRoundedIcon />
                          )
                        }
                        label={
                          dog.direct_booking_allowed
                            ? "Reserveren mogelijk"
                            : "Nog niet vrijgegeven"
                        }
                        color={
                          dog.direct_booking_allowed
                            ? "success"
                            : "default"
                        }
                      />
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Alert severity="info">
              Er staat nog geen hond aan dit account gekoppeld.
            </Alert>
          )}
        </Card>

        <Card sx={{ p: 2.5, mt: 2.5 }}>
          <Typography variant="h5">
            Eerstvolgende reservering
          </Typography>

          {nextReservation ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mt: 1.5,
                boxShadow: "none",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                gap={1}
              >
                <Box>
                  <Typography fontWeight={600}>
                    {reservationLabel(nextReservation)}
                  </Typography>
                  <Typography color="text.secondary">
                    {nextReservation.reservation_dogs
                      .map((x) => x.dogs?.name)
                      .filter(Boolean)
                      .join(", ") || "Hond niet gevonden"}
                  </Typography>
                </Box>

                <Chip
                  label={statusLabel(nextReservation.status)}
                  color={
                    nextReservation.status === "confirmed"
                      ? "success"
                      : nextReservation.status === "option"
                        ? "warning"
                        : "default"
                  }
                />
              </Stack>
            </Paper>
          ) : (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Je hebt op dit moment geen komende reservering.
            </Alert>
          )}

          <Button
            sx={{ mt: 1.5 }}
            onClick={() => navigate("/mijn-pippaya/reserveringen")}
          >
            Alle reserveringen bekijken
          </Button>
        </Card>

        <Card sx={{ p: 2.5, mt: 2.5 }}>
          <Typography variant="h5">
            Zo werkt Mijn Pippaya
          </Typography>

          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            {[
              "Je klant- en hondenprofiel blijft gekoppeld aan jouw account",
              "Pippaya bepaalt per hond of rechtstreeks reserveren mogelijk is",
              "Je ziet je komende en eerdere reserveringen op één plek",
              "Nieuwe honden worden eerst aangemeld en beoordeeld",
            ].map((text, index) => (
              <Grid item xs={12} sm={6} key={text}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.75,
                    minHeight: 92,
                    boxShadow: "none",
                  }}
                >
                  <Typography fontWeight={600}>
                    {index + 1}. {text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Card>
      </Container>
    </Box>
  );
};
